/**
 * Point d'entrée taskpane Office — charge Office.js + onReady avant la sidebar.
 */
import React, { useEffect, useState } from 'react';
import { Spiner } from '../../../components';
import OfficeSidebar from './OfficeSidebar';
import { useOfficeTheme } from './useOfficeTheme';
import './OfficeSidebar.css';

const OFFICE_JS_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

async function markOfficeReady(): Promise<boolean> {
    if (typeof Office === 'undefined') return false;
    try {
        if (typeof (Office as any).onReady === 'function') {
            await (Office as any).onReady();
        }
        return true;
    } catch {
        return false;
    }
}

const OfficeSidebarEntry: React.FC = () => {
    const [officeJsReady, setOfficeJsReady] = useState(false);
    const [officeJsError, setOfficeJsError] = useState(false);

    useOfficeTheme(officeJsReady);

    useEffect(() => {
        document.documentElement.classList.add('office-embedded');
        document.body.classList.add('office-embedded');
        return () => {
            document.documentElement.classList.remove('office-embedded');
            document.body.classList.remove('office-embedded');
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const finishReady = async () => {
            const ok = await markOfficeReady();
            if (!cancelled) setOfficeJsReady(ok);
        };

        if (typeof Office !== 'undefined') {
            void finishReady();
            return () => {
                cancelled = true;
            };
        }

        const existing = document.getElementById('office-js') as HTMLScriptElement | null;
        if (existing) {
            const onLoad = () => {
                void finishReady();
            };
            const onError = () => {
                if (!cancelled) setOfficeJsError(true);
            };
            existing.addEventListener('load', onLoad);
            existing.addEventListener('error', onError);
            return () => {
                cancelled = true;
                existing.removeEventListener('load', onLoad);
                existing.removeEventListener('error', onError);
            };
        }

        const script = document.createElement('script');
        script.id = 'office-js';
        script.src = OFFICE_JS_URL;
        script.async = true;
        script.onload = () => {
            void finishReady();
        };
        script.onerror = () => {
            if (!cancelled) setOfficeJsError(true);
        };
        document.head.appendChild(script);

        return () => {
            cancelled = true;
        };
    }, []);

    if (officeJsError) {
        return (
            <div className="office-sidebar office-sidebar--error">
                <div className="office-sidebar__error-content">
                    <p>Office.js could not be loaded. Open this page from Outlook, Word, or Excel.</p>
                </div>
            </div>
        );
    }

    if (!officeJsReady) {
        return (
            <div className="office-sidebar office-sidebar--loading">
                <Spiner className="d-flex justify-content-center spinner-border-sm" />
            </div>
        );
    }

    return <OfficeSidebar />;
};

export default OfficeSidebarEntry;
